import { computed, inject, Injectable, INJECTOR, resource, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import { Store } from '@softline/core';
import { FieldOkComponentService } from '@softline/dynamic';
import { MessageBarService } from '@softline/ui-core';
import { firstValueFrom } from 'rxjs';
import { EslLinkStorageRepository } from '../repositories/esl-link-storage.repository';
import { ProductStorageLocation } from '../types/product-storage-location';
import { StorageAssociate } from '../types/storage-associate';
import { StorageLocation } from '../types/storage-location';
import { StorageLocationScanResult } from '../types/storage-location-scan-result';

@Injectable()
export class EslLinkStorageService {
  private readonly injector = inject(INJECTOR);
  private readonly repository = inject(EslLinkStorageRepository);
  private readonly fieldOkService = inject(FieldOkComponentService);
  private readonly messageService = inject(MessageBarService);
  private readonly store = inject(Store);

  readonly eslCode = signal<string | undefined>(undefined);
  readonly storageId = signal<string | number | undefined>(undefined);

  private readonly authenticationContext = this.store.signal<any | undefined>(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  );

  readonly #eslConfigResource = resource({
    params: () => ({ eslCode: this.eslCode() }),
    loader: async ({ params }) => {
      if (!params.eslCode) return Promise.resolve(null);
      try {
        return await this.repository.getEslConfig(params.eslCode);
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        return null;
      }
    },
  });

  readonly #storageResource = resource({
    params: () => ({ storageId: this.storageId() }),
    loader: async ({ params }) => {
      if (!params.storageId) return undefined;
      try {
        return await firstValueFrom(
          this.fieldOkService.loadResult(
            'lagerplatz',
            {
              filter: null,
              multiValued: false,
              parameters: {
                idvkforg: this.authenticationContext()?.vkforgid ?? 1,
              },
              maxAbfrageResults: 1000,
            },
            params.storageId
          )
        );
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        return undefined;
      }
    },
  });

  readonly isConfigLoading = this.#eslConfigResource.isLoading;
  readonly storageLocation = this.#storageResource.value;
  readonly labelType = computed(() => this.#eslConfigResource.value()?.labelType);

  readonly possibleItemCounts = computed(() => {
    const config = this.#eslConfigResource.value();
    const scenarios = config?.selectableScenarios.map((s) => s.itemCount) ?? [];
    return new Set(scenarios);
  });

  readonly selectableScenarios = computed(() => {
    const config = this.#eslConfigResource.value();
    const scenarios = config?.selectableScenarios ?? [];
    return scenarios;
  });

  async checkItemStorageLocation(eslCode: string, body: ProductStorageLocation) {
    try {
      return await this.repository.checkItemStorageLocation(eslCode, body);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return undefined;
    }
  }

  async changeItemStorageLocation(body: ProductStorageLocation) {
    try {
      await this.repository.changeItemStorageLocation(body);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async storageAssociate(body: StorageAssociate) {
    await this.repository.storageAssociate(body);
  }

  loading = signal<boolean>(false);

  async eslPreview(body: StorageAssociate) {
    try {
      this.loading.set(true);
      const request = await this.repository.eslPreview(body);
      this.loading.set(false);
      return request;
    } catch (error) {
      handleRequestError(error, this.injector, { show: true });
      this.loading.set(false);
      return null;
    }
  }

  async dissociate(eslCode: string): Promise<void> {
    return await this.repository.dissociate(eslCode);
  }

  async scanStorageLocation(
    storageLocationCode: string
  ): Promise<StorageLocationScanResult> {
    return await this.repository.scanStorageLocation(storageLocationCode);
  }

  async getAllStorageLocations(): Promise<StorageLocation[]> {
    const object = await this.repository.getAllStorageLocations();
    return object.data;
  }

  async getEslConfig(eslCode: string): Promise<any> {
    try {
      const eslConfig = await this.repository.getEslConfig(eslCode);
      this.#eslConfigResource.set(eslConfig);
      this.eslCode.set(eslCode);
      return eslConfig;
    } catch (e) {
      //TODO: add client translatable exception!!
      //handleRequestError(e, this.injector, { show: true });
      this.messageService.error('Diese ESL-Konfiguration konnte nicht geladen werden.');
    }
  }
}
